import { useState, useEffect } from 'react';
import Modal from '..';
import useStore from '../../Hooks/useStore';
import Form from '../../Form';
import { degrees, PDFDocument, PDFPage } from 'pdf-lib';
import { capitalizeFirstLetter, removeDuplicates } from '../../../store/utils';

type Image = {
  url: string;
  bytes: ArrayBuffer;
};

const PrintBannersModal = () => {
  const [getters, setters] = useStore();
  const {
    modal: { getModal },
    banner: { getBanner, getBackgroundImage, getPrintSettings },
    form: { getBannerConfigurations, getTransformations },
  } = getters;
  const {
    modal: { closeSingleModal },
    banner: { renderForms },
  } = setters;

  const singleBannerFormsToPrint: SingleBannerForm[] = getBanner(
    'singleBannerFormsToPrint',
  );

  const labeledForms = singleBannerFormsToPrint.reduce(
    (acc, form) => {
      acc[form.model.size][form.model.orientation].push(form);
      return acc;
    },
    {
      A4: {
        landscape: [],
        portrait: [],
      },
      A5: {
        landscape: [],
        portrait: [],
      },
      A6: {
        landscape: [],
        portrait: [],
      },
      A7: {
        landscape: [],
        portrait: [],
      },
      EP21: {
        landscape: [],
        portrait: [],
      },
    } as Record<SizeType, Record<OrientationsType, SingleBannerForm[]>>,
  );

  const forms: SingleBannerForm[] = singleBannerFormsToPrint;

  const modal = 'printBannerForm';
  const { defaultBackgroundImage } = getPrintSettings();
  const [enabledBackground, setEnabledBackground] = useState(
    defaultBackgroundImage,
  );
  const isOpen = getModal(`single.${modal}`);
  const onClose = () => closeSingleModal(modal);
  const [pdfUrl, setPdfUrl] = useState('');
  const [backgroundImages, setBackgroundImages] = useState<Image[]>([]);
  const [loadedBackgroundImages, setLoadedBackgroundImages] = useState(false);
  const [requestedBackgroundImages, setRequestBackgroundImages] =
    useState(false);
  const [creatingPdf, setCreatingPdf] = useState(true);
  const [isMultiple, setIsMultiple] = useState(true);

  useEffect(() => {
    if (!requestedBackgroundImages && enabledBackground) {
      Promise.all(
        [...new Set(forms.map((form) => getBackgroundImage(form)!))].map(
          (backgroundImageUrl) =>
            fetch(backgroundImageUrl).then(async (response) => ({
              url: backgroundImageUrl,
              bytes: await response.arrayBuffer(),
            })),
        ),
      ).then((response) => {
        setBackgroundImages(response);
        setLoadedBackgroundImages(true);
      });
      setRequestBackgroundImages(true);
    }
  }, [enabledBackground]);

  const createPdf = async () => {
    const xOldPriceBytes = await fetch('/xOldPrice.png').then(
      async (res) => await res.arrayBuffer(),
    );

    let fontData: { fontFamily: string; fontWeight: string }[] =
      removeDuplicates(
        forms.flatMap((form) =>
          getTransformations(100, form, 'pdf').map(
            ({ fontFamily, fontWeight }) => ({
              fontFamily: capitalizeFirstLetter(fontFamily),
              fontWeight: capitalizeFirstLetter(fontWeight),
            }),
          ),
        ),
      );

    const fonts = await Promise.all(
      fontData.map(({ fontFamily, fontWeight }) =>
        fetch(`/${fontFamily}-${fontWeight}.ttf`).then(async (res) => ({
          fontFamily,
          fontWeight,
          buffer: await res.arrayBuffer(),
        })),
      ),
    );

    let pdfBanners = await PDFDocument.create();

    if (isMultiple) {
      if (labeledForms['A4'].portrait.length > 0) {
        const [width, height] = [210 * 2.83467, 297 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A4'].portrait,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0) {
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage);
            count++;
          }
        }
      }

      if (labeledForms['A4'].landscape.length > 0) {
        const [width, height] = [297 * 2.83467, 210 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A4'].landscape,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0) {
              actualPage.setRotation(degrees(90));
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage);
            count++;
          }
        }
        actualPage.setRotation(degrees(90));
      }

      if (labeledForms['A5'].portrait.length > 0) {
        const [width, height] = [297 * 2.83467, 210 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A5'].portrait,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 2 === 0) {
              actualPage.setRotation(degrees(90));
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              x: ((count % 2) * width) / 2,
            });
            count++;
          }
        }
        actualPage.setRotation(degrees(90));
      }

      if (labeledForms['A5'].landscape.length > 0) {
        const [width, height] = [210 * 2.83467, 297 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A5'].landscape,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 2 === 0) {
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              y: height - (((count % 2) + 1) * height) / 2,
            });
            count++;
          }
        }
      }

      if (labeledForms['A6'].portrait.length > 0) {
        const [width, height] = [210 * 2.83467, 297 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A6'].portrait,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 4 === 0) {
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              x: ((count % 2) * width) / 2,
              y: height - ((Math.floor((count % 4) / 2) + 1) * height) / 2,
            });
            count++;
          }
        }
      }

      if (labeledForms['A6'].landscape.length > 0) {
        const [width, height] = [297 * 2.83467, 210 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A6'].landscape,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 4 === 0) {
              actualPage.setRotation(degrees(90));
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              x: ((count % 2) * width) / 2,
              y: height - ((Math.floor((count % 4) / 2) + 1) * height) / 2,
            });
            count++;
          }
        }
        actualPage.setRotation(degrees(90));
      }

      if (labeledForms['A7'].portrait.length > 0) {
        const [width, height] = [297 * 2.83467, 210 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A7'].portrait,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 8 === 0) {
              actualPage.setRotation(degrees(90));
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              x: ((count % 4) * width) / 4,
              y: height - ((Math.floor((count % 8) / 4) + 1) * height) / 2,
            });
            count++;
          }
          actualPage.setRotation(degrees(90));
        }
      }

      if (labeledForms['A7'].landscape.length > 0) {
        const [width, height] = [210 * 2.83467, 297 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['A7'].landscape,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 8 === 0) {
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              x: ((count % 2) * width) / 2,
              y: height - ((Math.floor((count % 8) / 2) + 1) * height) / 4,
            });
            count++;
          }
        }
      }

      if (labeledForms['EP21'].landscape.length > 0) {
        const [width, height] = [210 * 2.83467, 297 * 2.83467];
        const designedForms = await renderForms(
          labeledForms['EP21'].landscape,
          backgroundImages,
          fonts,
          enabledBackground,
          xOldPriceBytes,
        );
        let actualPage: PDFPage = await pdfBanners.addPage([width, height]);
        let count = 0;
        for (let i = 0; i < designedForms.length; i++) {
          const { form, pdfDocument } = designedForms[i];
          for (let j = 0; j < form.config.printer.copies; j++) {
            if (count !== 0 && count % 4 === 0) {
              actualPage = await pdfBanners.addPage([width, height]);
            }
            const pdfDocPage = pdfDocument.getPages()[0];
            const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
            await actualPage.drawPage(pdfEmbedPage, {
              y: height - (((count % 4) + 1) * height) / 4,
            });
            count++;
          }
        }
      }
    } else {
      const designedForms = await renderForms(
        singleBannerFormsToPrint,
        backgroundImages,
        fonts,
        enabledBackground,
        xOldPriceBytes,
      );
      for (let i = 0; i < designedForms.length; i++) {
        const { form, pdfDocument, pageWidth, pageHeight } = designedForms[i];
        for (let j = 0; j < form.config.printer.copies; j++) {
          let actualPage: PDFPage = await pdfBanners.addPage([
            pageWidth,
            pageHeight,
          ]);
          if (form.model.orientation === 'landscape') {
            actualPage.setRotation(degrees(90));
          }
          const pdfDocPage = pdfDocument.getPages()[0];
          const pdfEmbedPage = await pdfBanners.embedPage(pdfDocPage);
          await actualPage.drawPage(pdfEmbedPage);
        }
      }
    }

    const pdfBytes = await pdfBanners.save();
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);
    setPdfUrl(pdfBlobUrl);
    setCreatingPdf(false);
  };

  useEffect(() => {
    if ((enabledBackground && loadedBackgroundImages) || !enabledBackground) {
      setCreatingPdf(true);
      new Promise((resolve) =>
        setTimeout(
          () => {
            createPdf();
            resolve(true);
          },
          forms.length > 1 ? 200 : 0,
        ),
      );
    }
  }, [enabledBackground, loadedBackgroundImages, isMultiple]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      headerTitle="Visualização e Impressão"
    >
      <Form
        style={{
          display: 'flex',
          gap: '2rem',
          margin: '1rem 0 0 20px',
        }}
      >
        <Form.Input.Checkbox
          loading={
            creatingPdf || (enabledBackground && !loadedBackgroundImages)
          }
          className="form-banner-field"
          label="Incluir imagem de fundo"
          checked={enabledBackground}
          onChange={(e) => {
            setEnabledBackground(!enabledBackground);
          }}
        />

        <Form.Input.Checkbox
          loading={
            creatingPdf || (enabledBackground && !loadedBackgroundImages)
          }
          className="form-banner-field"
          label="Replicar"
          checked={isMultiple}
          onChange={() => {
            setIsMultiple(!isMultiple);
          }}
        />
      </Form>
      <iframe style={{ margin: '0px 20px 20px 20px' }} src={pdfUrl} />
    </Modal>
  );
};

export default PrintBannersModal;
